import { Analytics } from '@util/Analytics';
import {
  CONSENT_REOPEN_EVENT,
  Consent,
  ConsentManager,
  consentCategoryPreferences,
} from '@util/Consent';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../config/Color';

type OptionalPreferences = Pick<Consent, 'analytics' | 'advertising' | 'communications'>;
type PrivacyChoicesButtonProps = { className?: string };

function optionalPreferences(consent: Consent): OptionalPreferences {
  const preferences = consentCategoryPreferences(consent);
  return {
    analytics: preferences.analytics,
    advertising: preferences.advertising,
    communications: preferences.communications,
  };
}

export const PrivacyChoicesButton = ({ className }: PrivacyChoicesButtonProps): JSX.Element => (
  <button
    type="button"
    className={className}
    onClick={() => window.dispatchEvent(new Event(CONSENT_REOPEN_EVENT))}
    aria-haspopup="dialog"
  >
    Your Privacy Choices
  </button>
);

export const ConsentPreferences = (): JSX.Element => {
  const [show, setShow] = useState(false);
  const [preferences, setPreferences] = useState<OptionalPreferences>(
    optionalPreferences(ConsentManager.get())
  );

  const open = () => {
    setPreferences(optionalPreferences(ConsentManager.get()));
    setShow(true);
  };

  useEffect(function manageConsentReopen() {
    window.addEventListener(CONSENT_REOPEN_EVENT, open);
    window.showConsentBanner = open;
    return () => {
      window.removeEventListener(CONSENT_REOPEN_EVENT, open);
      delete window.showConsentBanner;
    };
  }, []);

  const choose = (next: OptionalPreferences) => {
    const saved = ConsentManager.set(next);
    setPreferences(optionalPreferences(saved));
    setShow(false);
    Analytics.initialize();
  };

  const setPreference = (key: keyof OptionalPreferences, value: boolean) => {
    setPreferences((current) => ({ ...current, [key]: value }));
  };

  return (
    <>
      {show && (
        <Backdrop>
          <Dialog role="dialog" aria-modal="true" aria-labelledby="privacy-preferences-title">
            <Header>
              <Title id="privacy-preferences-title">Privacy preferences</Title>
              <CloseButton type="button" onClick={() => setShow(false)} aria-label="Close">
                ×
              </CloseButton>
            </Header>
            <Description>
              We use optional cookies across our landing pages and assessments for analytics,
              advertising, and communications. Choose what you allow.
            </Description>
            <Options>
              <Option>
                <OptionCopy>
                  <OptionTitle>Necessary</OptionTitle>
                  <OptionDescription>Required for the assessment to work.</OptionDescription>
                </OptionCopy>
                <input type="checkbox" checked disabled aria-label="Necessary cookies" />
              </Option>
              <Option>
                <OptionCopy>
                  <OptionTitle>Analytics</OptionTitle>
                  <OptionDescription>
                    Helps us understand and improve the experience.
                  </OptionDescription>
                </OptionCopy>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(event) => setPreference('analytics', event.target.checked)}
                  aria-label="Analytics cookies"
                />
              </Option>
              <Option>
                <OptionCopy>
                  <OptionTitle>Advertising</OptionTitle>
                  <OptionDescription>
                    Measures campaigns and relevant advertising.
                  </OptionDescription>
                </OptionCopy>
                <input
                  type="checkbox"
                  checked={preferences.advertising}
                  onChange={(event) => setPreference('advertising', event.target.checked)}
                  aria-label="Advertising cookies"
                />
              </Option>
              <Option>
                <OptionCopy>
                  <OptionTitle>Communications</OptionTitle>
                  <OptionDescription>
                    Supports messages based on your interactions.
                  </OptionDescription>
                </OptionCopy>
                <input
                  type="checkbox"
                  checked={preferences.communications}
                  onChange={(event) => setPreference('communications', event.target.checked)}
                  aria-label="Communications cookies"
                />
              </Option>
            </Options>
            <Actions>
              <PrimaryButton type="button" onClick={() => choose(preferences)}>
                Save preferences
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={() => choose({ analytics: true, advertising: true, communications: true })}
              >
                Accept all
              </SecondaryButton>
              <TextButton
                type="button"
                onClick={() =>
                  choose({ analytics: false, advertising: false, communications: false })
                }
              >
                Reject non-essential
              </TextButton>
            </Actions>
          </Dialog>
        </Backdrop>
      )}
    </>
  );
};

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.5);
`;

const Dialog = styled.div`
  width: min(520px, 100%);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  border-radius: 16px;
  background: #ffffff;
  color: #1c312d;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.24);
  padding: 24px;
  text-align: left;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 28px;
  line-height: 1;
`;

const Description = styled.p`
  margin: 16px 0;
  line-height: 1.5;
`;

const Options = styled.div`
  border-top: 1px solid #e5e7eb;
`;

const Option = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 14px 0;
  border-bottom: 1px solid #e5e7eb;

  input {
    width: 20px;
    height: 20px;
    accent-color: ${Color.action.default};
  }
`;

const OptionCopy = styled.span`
  display: grid;
  gap: 4px;
`;

const OptionTitle = styled.span`
  font-weight: 600;
`;

const OptionDescription = styled.span`
  color: #5f6b68;
  font-size: 13px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`;

const PrimaryButton = styled.button`
  appearance: none;
  border: 0;
  border-radius: 999px;
  background: ${Color.action.default};
  color: #ffffff;
  cursor: pointer;
  font-weight: 600;
  padding: 10px 18px;
`;

const SecondaryButton = styled(PrimaryButton)`
  background: ${Color.action.muted};
  color: #1c312d;
`;

const TextButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: ${Color.action.default};
  cursor: pointer;
  padding: 10px 8px;
  text-decoration: underline;
`;
